///////////////////////////////////////////////////////////////////////////////////
// Standard
///////////////////////////////////////////////////////////////////////////////////

/*
•	The %rMT variable shall be editable from 1% to a maximum operating limit of 200%
o	The device UI software shall indicate to the user that the limit is reached if this occurs. 
o	For a new, blank, treatment template the variable shall default to 120%rMT.
o	The step size shall be 1% increments.
*/
export const UI_STANDARD_PC_MT = { min: 1, max: 200, default: 120, step: 1 }
/*
•	The pulses per second (frequency, PPS) variable shall allow the user to specify a value from 1 to FMAX PPS. 
o	For frequencies below 30PPS the variable step size shall be 0.1PPS. For frequencies from 30PPS and above, the variable step size shall be 1PPS. 
o	FMAX can be up to 100PPS depending on the system configuration and the power set. 
o	The default frequency shall be 10PPS or FMAX if it is less than 10PPS.
Changed max from 100 --> 60 with #731 - Limit PPS to 60 for Standard Repetitive https://lucidgroup.assembla.com/spaces/magstim/tickets/731/details?tab=activity
*/
export const UI_STANDARD_PULSES_PER_SEC = { min: 1, max: 60, default: 10, step: undefined };

/*
•	The On-time variable shall allow the user to set the duration of the train between 0.1s and 600.0s.
o	 For times less than 30.0s the step size shall be 0.1s. For times from 30.0s and above the step size shall be 1.0s. 
o	The maximum on-time can be up to 600.0s depending on system configuration, along with the currently set power and pulses per second values.
o	If the on-time is specified, then the Number of Pulses are automatically calculated.
o	The default on-time shall be 4.0s.
o	The maximum on-time shall be limited to ensure a maximum off-time (wait time) greater than 540.0s is not possible.
*/
export const UI_STANDARD_ON_TIME = { min: 0.1, max: 600, default: 4.0, step: undefined };

/*
•	The number of pulses variable (pulses per train) shall allow the user to set the number of pulses in a train from 1 – 60,000, provided that the duration does not exceed the maximum allowed.
o	If the number of pulses is specified, then the on-time is automatically calculated.
o	The default number of pulses shall be 40.
o	The step size shall be 1.
*/
export const UI_STANDARD_PULSES_PER_TRAIN = { min: 1, max: 60000, default: 40, step: 1 };

/*
•	The off-time variable shall allow the user to specify an inter-train delay or wait time from 0.5s to 540.0s. 
o	The default off-time shall be 11.0s.
o	The step size shall be 0.1s.
*/
export const UI_STANDARD_OFF_TIME = { min: 0.5, max: 540, default: 26.0, step: 0.1 };

/*
•	The total number of trains variable shall allow the user to set the number of times the specified train runs from 1 – 999.
o	The default number of trains shall be 1.
o	The maximum number of trains for a Horizon system shall limit the total number of pulses to 60,000 or less.
o	The step size shall be 1.
*/
export const UI_STANDARD_TOTAL_TRAINS = { min: 1, max: 999, default: 75, step: 1 };

/*
•	The charge delay function shall be toggleable on and off, with the editable parameter disabled when the function is toggled off.
o	When toggled on, the user shall be able to specify a duration from 1ms to 1000ms using the duration window.
o	The step size shall be 1ms.
o	When toggled off, the duration shall display 0ms and shall not be selectable.
o	Charge Delay functionality shall default to off.
o	The duration parameter shall default to 0ms.
*/
export const UI_STANDARD_CHARGE_DELAY = { min: 1, max: 1000, default: 0, step: 1 };

///////////////////////////////////////////////////////////////////////////////////
// Burst
///////////////////////////////////////////////////////////////////////////////////

/*
•	The %rMT variable shall be editable from 1% - 160%.
o	For a new, blank, treatment template the %rMT variable shall default to 120%rMT.
o	The step size shall be in 1% increments.
*/
export const UI_BURST_PC_MT = { min: 1, max: 160, default: 120, step: 1 }

/*
•	The pulses per second variable shall allow the user to set the frequency of the pulses from 10 – FMAX PPS.
o	The step size shall be 1PPS.
o	FMAX shall be defined as 50PPS for the Horizon System.
o	The variable shall default to 50PPS or FMAX if it is less than 50PPS.
*/
export const UI_BURST_PULSES_PER_SEC = { min: 10, max: 50, default: 50, step: 1 };

/*
•	The burst per second (BPS) variable shall allow the user to set the frequency of the bursts from 1BPS – 5BPS.
o	The step size shall be 1BPS.
o	The default value shall be 5BPS.
*/
export const UI_BURSTS_PER_SEC = { min: 1, max: 5, default: 5, step: 1 };

/*
•	The pulses per burst (PPB) variable shall allow the user to set a number of pulses in a burst from 1PPB – 3PPB.
o	The step size shall be 1PPB.
o	The default value shall be 3PPB.
*/
export const UI_PULSES_PER_BURST = { min: 1, max: 3, default: 3, step: 1 };

/*
•	The burst per cycle (BPC) variable shall allow the user to set the number of bursts in a cycle from 1BPC – 600BPC.
o	The step size shall be 1BPC.
o	The default value shall be 10BPC.
*/
export const UI_BURST_PER_CYCLE = { min: 1, max: 600, default: 10, step: 1 };

/*
•	The cycle time variable shall allow the user to set the duration of the cycle from 0.1s – 601.0s.
o	The step size shall be 0.1s.
o	The default value shall be 10.0s.
*/
export const UI_BURST_CYCLE_TIME = { min: 0.1, max: 601, default: 9.8, step: 0.1 };

/*
•	The total number of cycles variable shall allow the user to set the number of times the specified cycle runs from 1 – 999.
o	The step size shall be 1.
o	The default number of cycles shall be 20.
o	The default maximum number of cycles for a Horizon system shall limit the total number of pulses to 60,000 or less.
*/
export const UI_BURST_TOTAL_CYCLES = { min: 1, max: 999, default: 20, step: 1 };

/*
•	The charge delay function shall be toggleable on and off, with the editable parameter disabled when the function is toggled off.
o	When toggled on, the user shall be able to specify a duration from 1ms to 1000ms using the duration window.
o	The step size shall be 1ms.
o	When toggled off, the duration shall display 0ms and shall not be selectable.
o	Charge Delay functionality shall default to off.
o	The duration parameter shall default to 0ms.
*/
export const UI_BURST_CHARGE_DELAY = { min: 1, max: 1000, default: 0, step: 1 };

export const TOTAL_PULSES = 60000;